import apiClient from './client';

export interface LeadOpsMatch {
  item_id: string;
  board_id?: string;
  board_name?: string;
  group_name?: string;
  lead_status?: string;
  name?: string;
  url?: string;
  owner_id?: number | null;
  owner_name?: string | null;
  created_at?: string | null;
  last_activity_at?: string | null;
  domain?: string | null;
  domain_canonical?: string | null;
  domains?: string[];
  end_date?: string | null;
  days_to_end_date?: number | null;
  activity_log?: Array<{ created_at?: string | null; body?: string; entity?: string }>;
  activity_pending?: boolean;
  columns_preview?: Array<{ id?: string; title?: string; text?: string }>;
  linked_websites?: Array<{
    item_id?: string | null;
    board_id?: string | null;
    name?: string | null;
    domains?: string[];
  }>;
}

export interface LeadOpsCrmPayload {
  status: 'NOT_FOUND' | 'FOUND' | 'AMBIGUOUS' | 'ERROR';
  best_match?: LeadOpsMatch;
  matches?: LeadOpsMatch[];
  exact_match_found?: boolean;
  exact_lead_found?: boolean;
  exact_qualified_lead_found?: boolean;
  qualified_partner_found?: boolean;
  qualified_partner_match?: {
    item_id?: string | null;
    name?: string | null;
    match_reason: 'exact_domain' | 'website_relation' | 'bare_name' | string;
  } | null;
  error?: string;
}

export interface LeadOpsActivityLogResponse {
  item_id: string;
  board_id: string;
  activity_log: LeadOpsMatch['activity_log'];
  activity_available?: boolean;
  last_activity_at?: string | null;
  inactive_days?: number | null;
  can_reassign?: boolean;
  reassign_reason?: string | null;
}

export interface LeadOpsSuggestedActions {
  can_create: boolean;
  can_reassign: boolean;
  create_blocked_reason?: string | null;
  inactive_days: number | null;
  reassign_reason?: string | null;
}

export interface LeadOpsCheckSwResponse {
  domain_input: string;
  domain_canonical: string;
  similarweb: {
    summary?: Record<string, any>;
    raw_ref?: Record<string, any>;
    error?: string;
  };
}

export interface LeadOpsCheckMondayResponse {
  domain_input: string;
  domain_canonical: string;
  crm: LeadOpsCrmPayload;
  suggested_actions: LeadOpsSuggestedActions;
}

export interface AdvertiserOption {
  id: string;
  name: string;
}

export interface InventoryPitchPayload {
  inventory_item_id: string;
  inventory_item_name: string;
  advertiser_id: string;
  advertiser_name: string;
  expected_daily_volume?: number | null;
}

export interface InventoryPitchResponse {
  item_id: string;
  url?: string;
  deal_name?: string;
  duplicate_warning?: boolean;
}

export interface UnsoldInventoryResponse {
  critical: LeadOpsMatch[];
  ready: LeadOpsMatch[];
}

export const leadOpsApi = {
  checkSw: async (domain: string, mode: 'full' | 'light' = 'full') => {
    const response = await apiClient.post('/api/lead-ops/check-sw', { domain, mode });
    return response.data;
  },
  checkMonday: async (domain: string) => {
    const response = await apiClient.post('/api/lead-ops/check-monday', { domain });
    return response.data;
  },
  create: async (domain_canonical: string, sw_snapshot?: Record<string, any>) => {
    const response = await apiClient.post('/api/lead-ops/create', {
      domain_canonical,
      sw_snapshot,
    });
    return response.data;
  },
  reassign: async (item_id: string, board_id?: string, to_owner_id?: number) => {
    const response = await apiClient.post('/api/lead-ops/reassign', {
      item_id,
      board_id,
      to_owner_id,
    });
    return response.data;
  },
  markDuplicate: async (item_id: string, board_id: string, current_name: string) => {
    const response = await apiClient.post('/api/lead-ops/mark-duplicate', {
      item_id,
      board_id,
      current_name,
    });
    return response.data;
  },
  getConfig: async () => {
    const response = await apiClient.get('/api/lead-ops/config');
    return response.data;
  },
  getMe: async () => {
    const response = await apiClient.get('/api/lead-ops/me');
    return response.data;
  },
  getHistory: async (limit = 20) => {
    const response = await apiClient.get(`/api/lead-ops/history?limit=${limit}`);
    return response.data;
  },
  logout: async () => {
    await apiClient.post('/auth/logout', {});
    window.location.href = '/auth';
  },
  getTrafficSources: async (domain: string) => {
    const response = await apiClient.post('/api/lead-ops/traffic-sources', { domain });
    return response.data;
  },
  getCompetitors: async (domain: string) => {
    const response = await apiClient.post('/api/lead-ops/competitors', { domain });
    return response.data;
  },
  getActivityLog: async (item_id: string, board_id: string, limit = 50) => {
    const response = await apiClient.post('/api/lead-ops/activity-log', { item_id, board_id, limit });
    return response.data;
  },
  searchAdvertisers: async (q: string, limit = 10) => {
    const response = await apiClient.get('/api/lead-ops/advertisers', { params: { q, limit } });
    return response.data;
  },
  createInventoryPitch: async (payload: InventoryPitchPayload) => {
    const response = await apiClient.post('/api/lead-ops/inventory-pitch', payload);
    return response.data;
  },
  getUnsoldInventory: async () => {
    const response = await apiClient.get('/api/lead-ops/unsold-inventory');
    return response.data;
  },
};
